// src/lib/friendly-parse.ts
// ASCII only. Any special bullet char is written with \u2022.
//
// Friendly Paste format supported:
//   CueBoard #: Title
//   Triggers: a; b; c
//   Note: item1; item2; item3
//   (Note can also be multiline, with or without bullets.)
//
// Output: CueNote[] with id (slug), title, triggers[], body_markdown

import { CueNote } from "../types";

type Cur = {
  title?: string;
  triggers?: string[];
  bodyLines: string[];
};

export type FriendlyParseResult = {
  notes: CueNote[];
  errors: string[];
};

const BULLET_RE = /^[-*�]\s+/;

function stripLeadingBullet(s: string): string {
  return s.replace(BULLET_RE, "").trim();
}

function splitSemis(s: string): string[] {
  return s
    .split(";")
    .map(v => v.trim())
    .filter(Boolean);
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function finalizeBody(raw: string): string {
  let body = raw.trim();

  if (!body) return "";

  // If there are explicit newlines, decide whether to normalize bullets.
  if (/\n/.test(body)) {
    const lines = body.split("\n").map(l => l.trim()).filter(Boolean);
    const bulletish = lines.filter(l => BULLET_RE.test(l)).length;

    if (bulletish >= Math.ceil(lines.length * 0.6)) {
      // Normalize all bullet markers to "- "
      return lines.map(l => `- ${stripLeadingBullet(l)}`).join("\n");
    }
    // Not mostly bullets, return as paragraphs
    return lines.join("\n");
  }

  // Single line. If semicolon list, turn into bullets.
  if (body.includes(";")) {
    return splitSemis(body)
      .map(stripLeadingBullet)
      .map(v => `- ${v}`)
      .join("\n");
  }

  // Otherwise keep as a single paragraph
  return body;
}

export function parseFriendlyNotes(input: string): FriendlyParseResult {
  const errors: string[] = [];
  const notes: CueNote[] = [];

  const text = (input || "").replace(/\r\n?/g, "\n");
  const lines = text.split("\n");

  let cur: Cur | null = null;

  function pushCur() {
    if (!cur) return;
    if (!cur.title) {
      errors.push("Missing cue title before body or triggers.");
      cur = null;
      return;
    }
    const title = cur.title.trim();
    const idBase = slugify(title) || "cue";
    let id = idBase;
    let suffix = 2;
    while (notes.some(n => n.id === id)) {
      id = `${idBase}_${suffix++}`;
    }

    const triggers = (cur.triggers || []).map(t => t.trim()).filter(Boolean);
    const body_markdown = finalizeBody(cur.bodyLines.join("\n"));

    notes.push({
      id,
      title,
      triggers,
      body_markdown,
    });

    cur = null;
  }

  for (const rawLine of lines) {
    const l = rawLine.trim();
    if (!l) continue;

    // Title markers
    const cueMatch = /^cueboard\s*\d*\s*:\s*(.+)$/i.exec(l);
    const h1Match = /^#\s*(.+)$/.exec(l);
    const titleMatch = /^title:\s*(.+)$/i.exec(l);

    const titlePick = cueMatch?.[1] ?? h1Match?.[1] ?? titleMatch?.[1];
    if (titlePick) {
      pushCur();
      cur = { title: titlePick.trim(), bodyLines: [] };
      continue;
    }

    // Triggers
    const trigMatch = /^triggers?:\s*(.+)$/i.exec(l);
    if (trigMatch) {
      if (!cur) cur = { bodyLines: [] };
      cur.triggers = splitSemis(trigMatch[1] ?? "");
      continue;
    }

    // Note header or inline note line
    const noteHeader = /^notes?:\s*(.*)$/i.exec(l);
    if (noteHeader) {
      if (!cur) cur = { bodyLines: [] };
      const first = (noteHeader[1] ?? "").trim();
      if (first) cur.bodyLines.push(first);
      continue;
    }

    // Body line
    if (!cur) cur = { bodyLines: [] };
    cur.bodyLines.push(l);
  }

  // Flush last
  pushCur();

  return { notes, errors };
}